import { CreateLocalProduct } from '../components/CreateLocalProduct';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  DataTable,
  TextField,
  Banner,
  Pagination,
  EmptyState,
  Spinner,
  Thumbnail,
  Link,
} from '@shopify/polaris';
import { ImageIcon } from '@shopify/polaris-icons';
import { useApi } from '../hooks/useApi';

interface Product {
  id: number;
  shopify_product_id: string;
  shopify_variant_id: string;
  sku: string;
  barcode: string;
  title: string;
  requires_shipping: number;
  track_inventory: number;
  image_url: string | null;
  total_quantity: number;
  allocated: number;
  available: number;
}

interface ProductsResponse {
  products: Product[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export default function Products() {
  const navigate = useNavigate();
  const { fetchApi, loading, error } = useApi();
  const [products, setProducts] = useState<Product[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [initialLoad, setInitialLoad] = useState(true);

  const loadProducts = useCallback(async (searchTerm?: string, pageNum?: number) => {
    const params = new URLSearchParams({
      page: String(pageNum ?? page),
      limit: '50',
    });

    const query = searchTerm ?? searchQuery;
    if (query) {
      params.set('search', query);
    }

    const data = await fetchApi<ProductsResponse>(`/products?${params.toString()}`);
    if (data) {
      setProducts(data.products || []);
      setTotalCount(data.total || 0);
      setTotalPages(data.totalPages || 0);
    }
    setInitialLoad(false);
  }, [fetchApi]);

  useEffect(() => {
    loadProducts(searchQuery, page);
  }, [page, loadProducts]);

  const handleSearch = () => {
    setPage(1);
    loadProducts(searchQuery, 1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  if (initialLoad && loading) {
    return (
      <Page title="Products">
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <Spinner size="large" />
                <Text as="p" tone="subdued">Loading products...</Text>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title="Products"
      subtitle={`${totalCount.toLocaleString()} products in this warehouse account`}
      primaryAction={{
        content: 'Sync Products',
        url: '/initial-sync',
      }}
    >
      <Layout>
        <Layout.Section><CreateLocalProduct onCreated={() => void loadProducts()} /></Layout.Section>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {/* Search */}
        <Layout.Section>
          <Card>
            <InlineStack gap="400" blockAlign="end">
              <div style={{ flex: 1 }} onKeyDown={handleKeyDown}>
                <TextField
                  label="Search"
                  labelHidden
                  placeholder="Search by SKU, barcode, or product name..."
                  value={searchQuery}
                  onChange={setSearchQuery}
                  autoComplete="off"
                  connectedRight={
                    <Button onClick={handleSearch} loading={loading}>
                      Search
                    </Button>
                  }
                />
              </div>
            </InlineStack>
          </Card>
        </Layout.Section>

        {/* Products Table */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              {products.length > 0 ? (
                <>
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'numeric', 'numeric', 'text']}
                    headings={['', 'SKU', 'Product', 'Available', 'Allocated', 'Shopify']}
                    rows={products.map((product) => [
                      <div
                        key={`img-${product.id}`}
                        onClick={() => navigate(`/products/${product.id}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        {product.image_url ? (
                          <Thumbnail
                            source={product.image_url}
                            alt={product.title}
                            size="small"
                          />
                        ) : (
                          <Thumbnail
                            source={ImageIcon}
                            alt="No image"
                            size="small"
                          />
                        )}
                      </div>,
                      product.sku || '-',
                      <Link
                        key={`title-${product.id}`}
                        onClick={() => navigate(`/products/${product.id}`)}
                        monochrome
                        removeUnderline
                      >
                        {product.title.length > 60
                          ? product.title.substring(0, 60) + '...'
                          : product.title}
                      </Link>,
                      <InlineStack gap="200" blockAlign="center" key={`avail-${product.id}`}>
                        <Text as="span" fontWeight={product.available <= 0 ? 'bold' : 'regular'}>
                          {product.available}
                        </Text>
                        {product.available <= 0 && (
                          <Badge tone="critical">Out of stock</Badge>
                        )}
                        {product.available > 0 && product.available <= 5 && (
                          <Badge tone="warning">Low</Badge>
                        )}
                      </InlineStack>,
                      product.allocated > 0 ? (
                        <Badge tone="info" key={`alloc-${product.id}`}>{String(product.allocated)}</Badge>
                      ) : (
                        <Text as="span" tone="subdued" key={`alloc-${product.id}`}>0</Text>
                      ),
                      product.shopify_variant_id.startsWith('internal:') ? <Button key={`link-${product.id}`} onClick={async () => {
                        const result = await fetchApi<{ success: boolean }>(`/products/${product.id}/link-shopify`,{ method:'POST' });
                        if (result?.success) void loadProducts();
                      }}>Link matching SKU</Button> : 'Linked',
                    ])}
                  />

                  <InlineStack align="center" gap="400">
                    <Pagination
                      hasPrevious={page > 1}
                      hasNext={page < totalPages}
                      onPrevious={() => setPage(page - 1)}
                      onNext={() => setPage(page + 1)}
                    />
                    <Text as="span" tone="subdued">
                      Page {page} of {totalPages} ({totalCount.toLocaleString()} products)
                    </Text>
                  </InlineStack>
                </>
              ) : (
                <EmptyState
                  heading="No products found"
                  action={{
                    content: 'Import Products',
                    url: '/initial-sync',
                  }}
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <p>
                    Products will appear here after you sync them from Shopify.
                    Go to Data Import to get started.
                  </p>
                </EmptyState>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
